/**
 * IconButton atom - Circular button for icons.
 * Used for camera controls, action buttons, etc.
 *
 * Features:
 * - Glass effect option
 * - Glow effect for emphasis
 * - Micro-interaction animations
 * - Badge support for notifications
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'filled' | 'ghost' | 'glass';
  label: string; // Required for accessibility
  badge?: number | boolean; // Show notification badge
  glow?: boolean; // Add glow effect
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({
    icon,
    size = 'md',
    variant = 'default',
    label,
    badge,
    glow = false,
    disabled,
    className = '',
    ...props
  }, ref) => {
    const baseStyles = `
      relative inline-flex items-center justify-center rounded-full
      transition-all duration-150 ease-out
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
      disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
      active:scale-95
      tap-highlight-none
    `;

    const sizeStyles = {
      sm: 'h-8 w-8 text-lg',
      md: 'h-10 w-10 text-xl',
      lg: 'h-12 w-12 text-2xl',
      xl: 'h-14 w-14 text-3xl',
    };

    const variantStyles = {
      default: `
        bg-surface/90 backdrop-blur-sm text-text
        hover:bg-surface-hover hover:shadow-elevation-1
        border border-border-light
      `,
      filled: `
        bg-primary text-on-primary
        hover:bg-primary-hover hover:shadow-elevation-1
        shadow-sm
      `,
      ghost: `
        bg-transparent text-text
        hover:bg-surface-hover/70
      `,
      glass: `
        glass text-text
        hover:bg-glass-bg/90
      `,
    };

    const glowStyles = glow ? 'shadow-glow-sm' : '';

    // Badge content
    const showBadge = badge !== undefined && badge !== false && badge !== 0;
    const badgeContent = typeof badge === 'number' ? (badge > 99 ? '99+' : badge) : '';

    return (
      <button
        ref={ref}
        disabled={disabled}
        aria-label={label}
        className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${glowStyles} ${className}`}
        {...props}
      >
        {icon}
        {showBadge && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-error text-[10px] font-bold text-on-primary animate-scale-in">
            {badgeContent}
          </span>
        )}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
